import React, {
  useEffect,
  useState,
  useCallback,
  useRef,
  useMemo,
} from "react";
import { InputText } from "primereact/inputtext";
import { InputNumber } from "primereact/inputnumber";
import { InputTextarea } from "primereact/inputtextarea";
import { Dropdown } from "primereact/dropdown";
import { MultiSelect } from "primereact/multiselect";
import { Calendar } from "primereact/calendar";
import { OverlayPanel } from "primereact/overlaypanel";
import { Checkbox } from "primereact/checkbox";
import { Dialog } from "primereact/dialog";
import { AutoComplete } from "primereact/autocomplete";
import { Tag } from "primereact/tag";
import { Tooltip } from "primereact/tooltip";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import InboxOutlinedIcon from "@mui/icons-material/InboxOutlined";
import CheckCircleOutlinedIcon from "@mui/icons-material/CheckCircleOutlined";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import PropTypes from "prop-types";
import { format, subYears, isValid } from "date-fns";

import { useAuth } from "context/AuthContext";
import exportAsCsv from "utils/exportAsCsv";
import PrimeReactDataGrid from "components/PrimeReactDataGrid/PrimeReactDataGrid";
import styles from "./ClassRosterGridV2.module.css";
import {
  getClassTraineeMilestoneDetails,
  getMilestoneByRosterId,
  updateClassRosterCurriculumStatus,
  getCurriculumStatus,
  getTraineeStatusList,
  getClassRosterDetails,
  getSearchPopoverContent,
  updateClassTraineeMilestoneGridDetails,
  bulkUpdateClassTraineeMilestoneGridDetails,
  getSearchValue,
} from "../../../services/learnPointUserServices";
import BackdropComponent from "components/Spinner/Spinner";
import { useDispatch } from "react-redux";
import { SnackBarStoreAction } from "Store/SnackBar";
import SnackBar from "components/SnackBar/SnackBar";

// ─────────────────────────────────────────────────────────────────────────────
// CONSTANTS
// ─────────────────────────────────────────────────────────────────────────────

const ALLOWED_ROLES = [
  "LD_Leader",
  "LD_Trainer",
  "LD_Group",
  "LD_Group_Admin",
  "Ops_Group",
];

// Computed once at module level — stable across all renders
const ONE_YEAR_AGO = subYears(new Date(), 1);

// Fields that are never editable — excluded from change detection and save calls
const READ_ONLY_FIELDS = new Set([
  "id",
  "ClassTraineeId",
  "TraineeOracleId",
  "TraineeName",
  "TraineeStatusId", // numeric ID — TraineeStatus (label) is the editable field
  "EmployeeStatus",
  "TraineeComment",
  "FTRR", // read-only per spec
  "Project",
  "Site",
  "HasThirdPartyUpdate",
  "OperationalTeamLeadId",
]);

// Initial filter: show only Active trainees by default.
const INITIAL_FILTER_PROPS = {
  TraineeStatus: { value: "Active", matchMode: "equals" },
};

// ─────────────────────────────────────────────────────────────────────────────
// BULK EDIT — field descriptors for the multi-column bulk edit dialog.
// Static editable columns are listed here. Dynamic milestone columns are
// merged in at runtime from the `columnDefs` state.
// ─────────────────────────────────────────────────────────────────────────────

const BULK_EDITABLE_FIELDS = [
  { field: "TraineeStatus",           label: "Trainee Status",   type: "traineeStatus" },
  { field: "EndorsedToNestingStatus", label: "E2N",              type: "boolean"       },
  { field: "E2P",                     label: "E2P",              type: "boolean"       },
  { field: "OperationLeadName",       label: "Operation Lead",   type: "opsLead"       },
  { field: "ProductionDate",          label: "Production Date",  type: "date"          },
];

/** Returns the initial per-field state for the bulk dialog (all disabled, null values). */
const emptyBulkState = () =>
  Object.fromEntries(
    BULK_EDITABLE_FIELDS.map((f) => [f.field, { enabled: false, value: null }]),
  );

// ─────────────────────────────────────────────────────────────────────────────
// PURE UTILITY HELPERS
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Serialize a cell value into the string format the update API expects.
 *   null / "" / undefined  →  "null"
 *   Array (empty)          →  "multiSelectNoValue"
 *   Array (filled)         →  comma-joined string
 *   Date (valid)           →  "MM/dd/yyyy"
 *   anything else          →  .toString()
 */
const serializeValue = (value) => {
  if (value === null || value === undefined || value === "") return "null";
  if (typeof value === "string" && value.trim() === "") return "null";
  if (Array.isArray(value))
    return value.length > 0 ? value.join(",") : "multiSelectNoValue";
  if (value instanceof Date)
    return isValid(value) ? format(value, "MM/dd/yyyy") : "null";
  return value.toString();
};

/**
 * Parse the milestone column type metadata string from getMilestoneByRosterId.
 *   "multiselect-Good,Average,Poor"  →  { type: "multiselect", options: ["Good","Average","Poor"] }
 *   "date-NA"                        →  { type: "date", options: [] }
 *   "memo-NA"                        →  { type: "memo", options: [] }
 */
const parseMilestoneType = (raw) => {
  const dashIdx = raw.indexOf("-");
  const type = raw.slice(0, dashIdx).trim().toLowerCase();
  const optionsPart = raw.slice(dashIdx + 1).trim();
  const options =
    optionsPart && optionsPart !== "NA" ? optionsPart.split(",") : [];
  return { type, options };
};

/** Debounce factory — no `this` leak, safe inside any arrow function. */
const debounce = (fn, ms) => {
  let timer;
  return (...args) => {
    clearTimeout(timer);
    timer = setTimeout(() => fn(...args), ms);
  };
};

/**
 * Filter "added in error" rows and attach a stable `id` field.
 * PrimeReact's cell-edit tracking requires a `dataKey` — we use `id`.
 */
const normalizeRows = (data) =>
  data
    .filter((r) => r.TraineeStatus?.toLowerCase() !== "added in error")
    .map((r) => ({ ...r, id: r.ClassTraineeId }));

// ─────────────────────────────────────────────────────────────────────────────
// CELL EDITOR COMPONENTS
// Defined outside RosterGrid so they are not re-registered on every render.
// In cell-edit mode, PrimeReact correctly updates opts.rowData between
// re-renders, so all editors can read directly from opts.rowData — no
// makeTrackedEditor / editedValuesRef workaround required.
// ─────────────────────────────────────────────────────────────────────────────

/** Memo (multiline) editor with a live character counter — 4000-char limit */
const MEMO_MAX_LENGTH = 4000;

const MemoEditor = ({ value, editorCallback }) => (
  <div className={styles.memoEditor}>
    <InputTextarea
      value={value || ""}
      onChange={(e) => editorCallback(e.target.value)}
      rows={2}
      maxLength={MEMO_MAX_LENGTH}
      autoResize
      className={styles.memoTextarea}
    />
    <span
      className={`${styles.charCount} ${(value?.length ?? 0) >= MEMO_MAX_LENGTH ? styles.charLimit : ""}`}
    >
      {value?.length ?? 0}/{MEMO_MAX_LENGTH}
    </span>
  </div>
);

MemoEditor.propTypes = {
  value: PropTypes.string,
  editorCallback: PropTypes.func.isRequired,
};

/**
 * Date picker with a hard lower bound of 1 year ago.
 *
 * Cell-edit migration note:
 *   Validation is now entirely self-contained — DateEditor shows its own
 *   inline error and simply does not call editorCallback for invalid dates.
 *   onCellEditComplete will therefore receive the original value unchanged,
 *   which the save handler treats as a no-op.  No parent-level dateError
 *   state or blocking logic needed.
 */
const DateEditor = ({ value, editorCallback }) => {
  const [error, setError] = useState(false);
  const [replacing, setReplacing] = useState(false);
  const hasRawValue = value && value !== "null";
  const parsed = hasRawValue ? new Date(value) : null;
  // A legacy value from a prior Data Type doesn't parse as a date; show a
  // read-only tag with a Replace action instead of a blank Calendar.
  const isLegacyUnparseable = hasRawValue && !isValid(parsed);

  const handleChange = (e) => {
    const d = e.value;
    if (d === null) {
      setError(false);
      editorCallback(null);
      return;
    }
    if (d < ONE_YEAR_AGO) {
      setError(true);
      // Do NOT call editorCallback — leave the committed value unchanged
    } else {
      setError(false);
      editorCallback(d);
    }
  };

  if (isLegacyUnparseable && !replacing) {
    return (
      <div
        className={styles.dateEditorWrapper}
        style={{ display: "flex", alignItems: "center", gap: 6 }}
      >
        <Tag
          value={value}
          severity="warning"
          title={`Recorded before this Milestone's Data Type was changed: "${value}"`}
        />
        <Button size="small" onClick={() => setReplacing(true)}>
          Replace
        </Button>
      </div>
    );
  }

  return (
    <div className={styles.dateEditorWrapper}>
      <Calendar
        value={parsed}
        onChange={handleChange}
        dateFormat="mm/dd/yy"
        showButtonBar
        minDate={ONE_YEAR_AGO}
        showIcon
        inputClassName="rg-date-input"
        panelClassName="rg-date-panel"
      />
      {error && (
        <small className={styles.dateError}>Cannot be older than 1 year</small>
      )}
    </div>
  );
};

DateEditor.propTypes = {
  value: PropTypes.string,
  editorCallback: PropTypes.func.isRequired,
};

/**
 * Number editor. A legacy value from a prior Data Type isn't numeric, so
 * show a read-only tag with a Replace action instead of a blank input.
 */
const NumberEditor = ({ value, editorCallback }) => {
  const [replacing, setReplacing] = useState(false);
  const isLegacyUnparseable =
    value != null && value !== "" && isNaN(Number(value));

  if (isLegacyUnparseable && !replacing) {
    return (
      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
        <Tag
          value={value}
          severity="warning"
          title={`Recorded before this Milestone's Data Type was changed: "${value}"`}
        />
        <Button size="small" onClick={() => setReplacing(true)}>
          Replace
        </Button>
      </div>
    );
  }

  return (
    <InputNumber
      value={isLegacyUnparseable ? null : (value ?? null)}
      onValueChange={(e) => editorCallback(e.value)}
      useGrouping={false}
      className={styles.textInput}
      inputClassName={styles.textInput}
      allowEmpty
    />
  );
};

NumberEditor.propTypes = {
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  editorCallback: PropTypes.func.isRequired,
};

/** Chip-based multiselect editor */
const MultiSelectEditor = ({ value, options, editorCallback }) => {
  const selected = Array.isArray(value)
    ? value
    : value
      ? value
          .split(",")
          .map((v) => v.trim())
          .filter(Boolean)
      : [];

  return (
    <MultiSelect
      value={selected}
      options={options}
      onChange={(e) => editorCallback(e.value)}
      display="chip"
      placeholder="Select…"
      className={styles.multiselect}
      panelClassName="rg-multiselect-panel"
    />
  );
};

MultiSelectEditor.propTypes = {
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
  options: PropTypes.array.isRequired,
  editorCallback: PropTypes.func.isRequired,
};

/** Single-select dropdown (for dynamic singleselect milestone columns) */
const SingleSelectEditor = ({ value, options, editorCallback }) => (
  <Dropdown
    value={value}
    options={options}
    onChange={(e) => editorCallback(e.value)}
    placeholder="Select…"
    className={styles.dropdown}
    panelClassName="rg-dropdown-panel"
  />
);

SingleSelectEditor.propTypes = {
  value: PropTypes.string,
  options: PropTypes.array.isRequired,
  editorCallback: PropTypes.func.isRequired,
};

/**
 * TraineeStatusEditor — native <select> using numeric TraineeStatusId as value.
 *
 * ─── VALUE CONTRACT ──────────────────────────────────────────────────────────
 * The API expects the numeric status ID, not the label string:
 *   { fieldToUpdate: "TraineeStatus", valueToUpdate: "9" }
 *
 * So the editor operates entirely in ID space:
 *   • value prop  — the current row's TraineeStatusId (number)
 *   • <option value> — opt.value (number), cast to string by the DOM
 *   • editorCallback — called with the numeric ID (parsed from e.target.value)
 *   • onCellEditComplete receives the numeric ID → serializeValue → "9" ✓
 *
 * The display label is rendered as the <option> text child — the browser shows
 * it in the trigger and in the open list without any extra mapping.
 *
 * ─── DISABLED FLAG SEMANTICS (preserved from original) ───────────────────────
 *   opt.disabled: true  = role CAN select    → normal <option>
 *   opt.disabled: false = role CANNOT select → HTML disabled attr + greyed out
 *
 * ─── STYLING ─────────────────────────────────────────────────────────────────
 * .nativeStatusSelect matches the PrimeReact Dropdown trigger dimensions.
 * See ClassRosterGrid.module.css for the full rule.
 */
const TraineeStatusEditor = ({ value, options, editorCallback }) => {
  // value is a numeric TraineeStatusId; stringify for the controlled <select>
  const [selected, setSelected] = useState(value != null ? String(value) : "");

  const handleChange = (e) => {
    const raw = e.target.value;
    const numericId = parseInt(raw, 10);
    setSelected(raw); // keep <select> controlled
    editorCallback(numericId); // pass numeric ID — serialized to "9" by serializeValue
  };

  return (
    <select
      value={selected}
      onChange={handleChange}
      className={styles.nativeStatusSelect}
    >
      <option value="" disabled>
        Select status…
      </option>
      {options.map((opt) => (
        <option
          key={opt.value}
          value={String(opt.value)}
          disabled={!opt.disabled} // opt.disabled:true = CAN select → NOT disabled
        >
          {opt.label}
        </option>
      ))}
    </select>
  );
};

TraineeStatusEditor.propTypes = {
  value: PropTypes.number, // TraineeStatusId — numeric
  options: PropTypes.array.isRequired,
  editorCallback: PropTypes.func.isRequired,
};

/**
 * Operation Lead editor.
 * Self-contained: owns its own query/suggestions state.
 * Calls `onPersonSelected` when user picks a result so the parent can store
 * the OracleId for API serialization (cannot derive it from the display string).
 */
const OpsLeadEditor = ({ rowData, editorCallback, onPersonSelected }) => {
  const [query, setQuery] = useState(rowData.OperationLeadName || "");
  const [suggestions, setSuggestions] = useState([]);
  const [searching, setSearching] = useState(false);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedSearch = useCallback(
    debounce(async (q) => {
      if (!q || q.length < 3) return;
      setSearching(true);
      try {
        const res = await getSearchValue(q);
        if (res?.message === "Success") {
          setSuggestions(res.data.filter((i) => i.Email?.length > 0));
        }
      } finally {
        setSearching(false);
      }
    }, 300),
    [],
  );

  const handleSelect = (e) => {
    const person = e.value;
    const display = `${person.Name} (${person.Email})`;
    setQuery(display);
    editorCallback(display);
    onPersonSelected(person);
    setSuggestions([]);
  };

  return (
    <span
      className={`${styles.opsLeadEditor}${searching ? ` ${styles.opsLeadEditorSearching}` : ""}`}
    >
      <AutoComplete
        value={query}
        suggestions={suggestions}
        field="Name"
        itemTemplate={(item) => (
          <div className={styles.opsSuggestion}>
            <span className={styles.opsName}>{item.Name}</span>
            <span className={styles.opsEmail}>{item.Email}</span>
          </div>
        )}
        completeMethod={(e) => {
          setQuery(e.query);
          debouncedSearch(e.query);
        }}
        onSelect={handleSelect}
        onChange={(e) => {
          setQuery(
            typeof e.value === "string" ? e.value : (e.value?.Name ?? ""),
          );
        }}
        placeholder="Type 3+ chars to search…"
        className={styles.opsAutocomplete}
        inputClassName={styles.opsInput}
        minLength={3}
        delay={0}
        forceSelection={false}
        dropdown={false}
      />
      {searching && (
        <span className={styles.opsSpinner}>
          <CircularProgress size={13} thickness={5} sx={{ color: "#64748b" }} />
        </span>
      )}
    </span>
  );
};

OpsLeadEditor.propTypes = {
  rowData: PropTypes.object.isRequired,
  editorCallback: PropTypes.func.isRequired,
  onPersonSelected: PropTypes.func.isRequired,
};

// ─────────────────────────────────────────────────────────────────────────────
// CELL BODY / DISPLAY TEMPLATES
// ─────────────────────────────────────────────────────────────────────────────

const OracleIdTemplate = ({ rowData, onInfoClick }) => (
  <span className={styles.oracleCell}>
    {rowData.HasThirdPartyUpdate && (
      <span
        className={styles.infoIconAnchor}
        data-pr-tooltip="Click for 3rd-party tracking info"
        data-pr-position="top"
        onClick={(e) => onInfoClick(e, rowData)}
      >
        <InfoOutlinedIcon sx={{ color: "#3299ff" }} />
      </span>
    )}
    {rowData.TraineeOracleId}
  </span>
);

const TraineeNameTemplate = ({ rowData, onInfoClick }) => (
  <span
    className={`${styles.traineeName}${
      rowData.HasThirdPartyUpdate ? ` ${styles.traineeNameHasUpdate}` : ""
    }`}
    onClick={
      rowData.HasThirdPartyUpdate ? (e) => onInfoClick(e, rowData) : undefined
    }
  >
    {rowData.TraineeName}
  </span>
);

const TraineeStatusBodyTemplate = ({ rowData, traineeOptions }) => {
  const opt = traineeOptions.find((o) => o.value === rowData.TraineeStatusId);
  const label = opt?.label ?? rowData.TraineeStatus ?? "";
  const severity =
    label.toLowerCase() === "active"
      ? "success"
      : label.toLowerCase().includes("pending")
        ? "warning"
        : "secondary";
  return <Tag value={label} severity={severity} className="rg-status-tag" />;
};

/**
 * BooleanIconTemplate — displays a tick or cross MUI icon for boolean fields.
 *
 * Used for E2N (EndorsedToNestingStatus) and E2P — both editable columns.
 * Rendered inside <EditableCell> so the hover pencil cue signals editability.
 * No wrapper span or pill background — those belong to FtrrReadonlyCell only.
 *
 * Shares .ftrrIconTrue / .ftrrIconFalse CSS classes with FtrrReadonlyCell
 * so icon sizing and colour are consistent across all three boolean columns.
 */
const BooleanIconTemplate = ({ value }) =>
  value ? (
    <CheckCircleOutlinedIcon className={styles.ftrrIconTrue} />
  ) : (
    <CancelOutlinedIcon className={styles.ftrrIconFalse} />
  );

BooleanIconTemplate.propTypes = {
  value: PropTypes.oneOfType([PropTypes.bool, PropTypes.number]),
};

/**
 * FtrrReadonlyCell — read-only indicator for the FTRR field.
 *
 * Replaces the disabled PrimeReact Checkbox with a MUI SvgIcon pair:
 *   truthy  → CheckCircleOutlinedIcon  in green  (#16a34a)
 *   falsy   → CancelOutlinedIcon       in muted slate (#94a3b8)
 *
 * Rationale: a greyed-out checkbox requires the user to decode disabled
 * state before reading the value.  A tick/cross communicates the value
 * directly and unambiguously at a glance, while the read-only nature is
 * conveyed by the absence of the rg-editable-cell hover treatment (FTRR
 * has no editor prop so withCellEdit() never adds the pencil/hover cue).
 */
const FtrrReadonlyCell = ({ value }) => (
  <span className={styles.ftrrReadonly}>
    {value ? (
      <CheckCircleOutlinedIcon className={styles.ftrrIconTrue} />
    ) : (
      <CancelOutlinedIcon className={styles.ftrrIconFalse} />
    )}
  </span>
);

FtrrReadonlyCell.propTypes = {
  value: PropTypes.oneOfType([PropTypes.bool, PropTypes.number]),
};

const DateBodyTemplate = ({ value }) => {
  if (!value || value === "null")
    return <span className={styles.emptyCell}>—</span>;
  try {
    return <span>{format(new Date(value), "MM/dd/yyyy")}</span>;
  } catch {
    return <span className={styles.emptyCell}>—</span>;
  }
};

const MultiSelectBodyTemplate = ({ value }) => {
  const [expanded, setExpanded] = useState(false);

  if (!value) return <span className={styles.emptyCell}>—</span>;
  const items = Array.isArray(value)
    ? value
    : value
        .split(",")
        .map((v) => v.trim())
        .filter(Boolean);
  if (!items.length) return <span className={styles.emptyCell}>—</span>;

  const LIMIT = 2;
  const overflow = items.length - LIMIT;
  const visible = expanded ? items : items.slice(0, LIMIT);

  return (
    <span className={styles.chipRow}>
      {visible.map((v) => (
        <Tag key={v} value={v} className="rg-chip" />
      ))}
      {!expanded && overflow > 0 && (
        <Tag
          value={`+${overflow}`}
          className="rg-chip rg-chip-more rg-chip-toggle"
          onClick={(e) => {
            e.stopPropagation();
            setExpanded(true);
          }}
        />
      )}
      {expanded && (
        <Tag
          value="−"
          className="rg-chip rg-chip-more rg-chip-toggle"
          onClick={(e) => {
            e.stopPropagation();
            setExpanded(false);
          }}
        />
      )}
    </span>
  );
};

/**
 * EditableCell — wraps any cell body content with a hover-visible pencil icon.
 *
 * Uses MUI's EditOutlinedIcon (an SVG component) instead of a PrimeIcons
 * <i> element.  MUI SvgIcons are self-contained — no font loading dependency,
 * no codepoint fragility.  Size is controlled via font-size on the scoped
 * .editableCellIcon class; MUI SvgIcons default to 1em × 1em so they respond
 * to font-size exactly like text-based icons.
 *
 * Not applied to read-only columns (TraineeOracleId, TraineeName,
 * EmployeeStatus, TraineeComment, FTRR, Project, Site) — those have no
 * editor prop and withCellEdit() skips stamping rg-editable-cell on them.
 */

// ─────────────────────────────────────────────────────────────────────────────
// BULK EDIT — MODULE-LEVEL SUB-COMPONENTS
//
// Defined at module level (not inside the parent render function) so React
// never sees a new component type between renders. Defining them inline as
// `const` variables inside the component body is the root cause of focus loss:
// each render produces a new function reference → React unmounts/remounts the
// input → focus is lost after every keystroke.
// ─────────────────────────────────────────────────────────────────────────────

/**
 * AutoComplete-based people-picker for the OperationLeadName bulk field.
 * Owns its own query/suggestions state so it doesn't pollute the parent.
 * Calls `onChange(displayString)` on every keystroke for visual feedback and
 * `onPersonSelect(oracleId)` when the user picks a suggestion so the parent
 * can store the OracleId for the API call (cannot derive it from the display string).
 */
const BulkOpsLeadInput = ({ value, onChange, onPersonSelect, styles: s }) => {
  const [query, setQuery] = useState(value ?? "");
  const [suggestions, setSuggestions] = useState([]);
  const [searching, setSearching] = useState(false);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedSearch = useCallback(
    debounce(async (q) => {
      if (!q || q.length < 3) { setSuggestions([]); return; }
      setSearching(true);
      try {
        const res = await getSearchValue(q);
        if (res?.message === "Success") {
          setSuggestions(res.data.filter((i) => i.Email?.length > 0));
        }
      } finally {
        setSearching(false);
      }
    }, 300),
    [],
  );

  const handleChange = (e) => {
    const q = typeof e.value === "string" ? e.value : (e.value?.Name ?? "");
    setQuery(q);
    onChange(q);
    debouncedSearch(q);
  };

  const handleSelect = (e) => {
    const person = e.value;
    const display = `${person.Name} (${person.Email})`;
    setQuery(display);
    onChange(display);         // display string — for visual feedback only
    onPersonSelect(person.OracleId); // OracleId — what actually goes to the API
    setSuggestions([]);
  };

  return (
    <span className={s.bulkOpsLeadWrap}>
      <AutoComplete
        value={query}
        suggestions={suggestions}
        field="Name"
        itemTemplate={(item) => (
          <div className={s.opsSuggestion}>
            <span className={s.opsName}>{item.Name}</span>
            <span className={s.opsEmail}>{item.Email}</span>
          </div>
        )}
        completeMethod={(e) => debouncedSearch(e.query)}
        onSelect={handleSelect}
        onChange={handleChange}
        placeholder="Type 3+ chars to search…"
        inputClassName={s.bulkTextInput}
        minLength={3}
        delay={0}
        forceSelection={false}
        dropdown={false}
      />
      {searching && (
        <span className={s.opsSpinner}>
          <CircularProgress size={13} thickness={5} sx={{ color: "#64748b" }} />
        </span>
      )}
    </span>
  );
};

BulkOpsLeadInput.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  onPersonSelect: PropTypes.func.isRequired,
  styles: PropTypes.object.isRequired,
};

/**
 * A single checkbox-toggle + label + value-input row in the bulk edit dialog.
 * MUST live at module level — see note above.
 *
 * All closed-over state is passed explicitly as props:
 *   fieldDef        — { field, label, type, options }
 *   enabled         — boolean
 *   value           — current value for this field
 *   onToggle        — (checked: boolean) => void
 *   onValueChange   — (newValue: any) => void
 *   traineeOptions  — for the traineeStatus dropdown
 *   styles          — CSS module reference
 *   ONE_YEAR_AGO    — stable Date for Calendar minDate
 */
const BulkFieldRow = ({
  fieldDef,
  enabled,
  value,
  onToggle,
  onValueChange,
  onOpsLeadSelect,
  traineeOptions,
  styles: s,
  minDate,
}) => {
  const { field, label, type, options = [] } = fieldDef;

  return (
    <div className={s.bulkFieldRow}>
      <Checkbox
        checked={enabled}
        onChange={(e) => onToggle(e.checked)}
        inputId={`bulk-chk-${field}`}
      />
      <label
        htmlFor={`bulk-chk-${field}`}
        className={`${s.bulkFieldLabel}${enabled ? ` ${s.bulkFieldLabelActive}` : ""}`}
        title={label}
      >
        {label}
      </label>
      <div
        className={s.bulkFieldInput}
        style={{ opacity: enabled ? 1 : 0.35, pointerEvents: enabled ? "auto" : "none" }}
      >
        {type === "traineeStatus" && (
          <select
            value={value != null ? String(value) : ""}
            onChange={(e) => {
              const raw = e.target.value;
              onValueChange(raw === "" ? null : parseInt(raw, 10));
            }}
            className={s.nativeStatusSelect}
          >
            <option value="" disabled>Select status…</option>
            {traineeOptions.map((opt) => (
              <option
                key={opt.value}
                value={String(opt.value)}
                disabled={!opt.disabled}
              >
                {opt.label}
              </option>
            ))}
          </select>
        )}
        {type === "boolean" && (
          <Dropdown
            value={value}
            options={[
              { label: "Yes", value: true },
              { label: "No",  value: false },
            ]}
            optionLabel="label"
            optionValue="value"
            onChange={(e) => onValueChange(e.value)}
            placeholder="Select…"
            className={s.bulkDropdown}
            panelClassName="rg-dropdown-panel"
          />
        )}
        {type === "date" && (
          <Calendar
            value={value}
            onChange={(e) => onValueChange(e.value)}
            dateFormat="mm/dd/yy"
            showButtonBar
            minDate={minDate}
            showIcon
            inputClassName="rg-date-input"
            panelClassName="rg-date-panel"
            className={s.bulkCalendar}
          />
        )}
        {type === "opsLead" && (
          <BulkOpsLeadInput
            value={value ?? ""}
            onChange={onValueChange}
            onPersonSelect={onOpsLeadSelect}
            styles={s}
          />
        )}
        {(type === "text" || type === "default") && (
          <InputText
            value={value ?? ""}
            onChange={(e) => onValueChange(e.target.value)}
            placeholder={`Enter ${label.toLowerCase()}…`}
            className={s.bulkTextInput}
          />
        )}
        {(type === "memo" || type === "textarea") && (
          <div className={s.bulkMemoWrap}>
            <InputTextarea
              value={value ?? ""}
              onChange={(e) => onValueChange(e.target.value)}
              placeholder={`Enter ${label.toLowerCase()}…`}
              className={s.bulkTextInput}
              rows={2}
              maxLength={MEMO_MAX_LENGTH}
              autoResize
            />
            <span
              className={`${s.bulkMemoCount}${
                (value?.length ?? 0) >= MEMO_MAX_LENGTH ? ` ${s.bulkMemoCountLimit}` : ""
              }`}
            >
              {value?.length ?? 0}/{MEMO_MAX_LENGTH}
            </span>
          </div>
        )}
        {type === "number" && (
          <InputNumber
            value={value ?? null}
            onValueChange={(e) => onValueChange(e.value)}
            useGrouping={false}
            allowEmpty
            placeholder={`Enter ${label.toLowerCase()}…`}
            className={s.bulkTextInput}
            inputClassName={s.bulkTextInput}
          />
        )}
        {type === "singleselect" && (
          <Dropdown
            value={value}
            options={options}
            onChange={(e) => onValueChange(e.value)}
            placeholder="Select…"
            className={s.bulkDropdown}
            panelClassName="rg-dropdown-panel"
          />
        )}
        {type === "multiselect" && (
          <MultiSelect
            value={Array.isArray(value) ? value : []}
            options={options}
            onChange={(e) => onValueChange(e.value)}
            display="chip"
            placeholder="Select…"
            className={s.bulkDropdown}
            panelClassName="rg-multiselect-panel"
          />
        )}
        {/* Fallback: any unrecognised type → plain text */}
        {![
          "traineeStatus", "boolean", "date", "opsLead",
          "text", "default", "memo", "textarea",
          "number", "singleselect", "multiselect",
        ].includes(type) && (
          <InputText
            value={value ?? ""}
            onChange={(e) => onValueChange(e.target.value)}
            placeholder={`Enter ${label.toLowerCase()}…`}
            className={s.bulkTextInput}
          />
        )}
      </div>
    </div>
  );
};

BulkFieldRow.propTypes = {
  fieldDef:        PropTypes.object.isRequired,
  enabled:         PropTypes.bool.isRequired,
  value:           PropTypes.any,
  onToggle:        PropTypes.func.isRequired,
  onValueChange:   PropTypes.func.isRequired,
  onOpsLeadSelect: PropTypes.func.isRequired,
  traineeOptions:  PropTypes.array.isRequired,
  styles:          PropTypes.object.isRequired,
  minDate:         PropTypes.instanceOf(Date),
};

// ─────────────────────────────────────────────────────────────────────────────
// MAIN COMPONENT
// ─────────────────────────────────────────────────────────────────────────────

const RosterGridV2 = ({ classRosterId }) => {
  const { roles } = useAuth();

  // ── Metadata & header state ────────────────────────────────────────────────
  const [rosterDetails, setRosterDetails] = useState(null);
  const [projectName, setProjectName] = useState("—");
  const [curriculumOptions, setCurriculumOptions] = useState([]);
  const [curriculumValue, setCurriculumValue] = useState(null);
  const [traineeOptions, setTraineeOptions] = useState([]);

  // ── Grid data ──────────────────────────────────────────────────────────────
  const [rows, setRows] = useState([]);
  const [columnDefs, setColumnDefs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dataReady, setDataReady] = useState(false);

  // ── Bulk edit state ──────────────────────────────────────────────────────
  const [selectedRows, setSelectedRows] = useState([]);
  const [bulkDialogVisible, setBulkDialogVisible] = useState(false);
  const [bulkFieldState, setBulkFieldState] = useState(emptyBulkState);
  const [bulkSaving, setBulkSaving] = useState(false);

  /**
   * pendingOpsLeadRef — holds the selected person object from OpsLeadEditor
   * until onCellEditComplete fires for the OperationLeadName field.
   * Shape: { rowId: number, person: { OracleId, Name, Email } } | null
   *
   * This is the only ref retained from the row-edit architecture.
   * It is required because cell-edit's onCellEditComplete only surfaces the
   * display string (newValue) — the numeric OracleId needed by the API must
   * be captured at selection time and held here.
   */
  const pendingOpsLeadRef = useRef(null);

  // ── Bulk ops lead OracleId ref ────────────────────────────────────────────
  // Holds the OracleId selected in the bulk dialog's OperationLeadName picker.
  // bulkFieldState stores only the display string (for the input's visual state);
  // this ref is what applyBulkEdit serializes to the API — mirrors the pattern
  // used by pendingOpsLeadRef for single-cell edits.
  const bulkOpsLeadOracleIdRef = useRef(null);

  // ── Overlay ref ───────────────────────────────────────────────────────────
  const overlayRef = useRef(null);
  const [popoverHtml, setPopoverHtml] = useState("");
  const dispatch = useDispatch();

  // ─── Snack helper ──────────────────────────────────────────────────────────

  const [snack, setSnack] = useState({
    open: false,
    message: "",
    severity: "",
  });

  const snackReset = () => {
    setSnack({ open: false, message: "", severity: "" });
  };

  const showSnack = useCallback(
    (message, severity) => {
      dispatch(
        SnackBarStoreAction.updateSnack({ open: true, message, severity }),
      );
    },
    [dispatch],
  );

  // ─── Role check ────────────────────────────────────────────────────────────

  const checkRole = useCallback(
    (role) => ALLOWED_ROLES.includes(role) && roles.includes(role),
    [roles],
  );

  // ─────────────────────────────────────────────────────────────────────────
  // DATA FETCHING
  // ─────────────────────────────────────────────────────────────────────────

  const fetchTraineeOptions = useCallback(async () => {
    try {
      const res = await getTraineeStatusList();
      if (res?.message !== "Success") return [];
      return res.data.map((item) => {
        const label = item.label.toLowerCase();
        const isAdmin = checkRole("LD_Group_Admin");
        const isTrainer = checkRole("LD_Trainer") || checkRole("LD_Group");
        const isLeader = checkRole("LD_Leader");

        const disabled =
          (label === "active" && isAdmin) ||
          (label === "added in error" && isAdmin) ||
          (label === "pending r/t" && (isAdmin || isTrainer || isLeader));

        return {
          ...item,
          disabled,           // true = role CAN select (used by TraineeStatusEditor native <select>)
          isDisabled: !disabled, // true = role CANNOT select (used by bulk Dropdown optionDisabled)
        };
      });
    } catch {
      return [];
    }
  }, [checkRole]);

  // Tracks the roster whose data has already been loaded, so the load effect
  // does not re-fire (and re-hit the same endpoints) when unstable hook deps
  // — e.g. a fresh `roles` array from useAuth on an auth-context re-render —
  // change loadAll's identity. This also prevents an automatic duplicate call
  // on failure. A genuine roster change still remounts this grid via
  // `key={classRosterId}` (set by the parent), which resets this ref.
  const loadedRosterRef = useRef(null);

  const loadAll = useCallback(async () => {
    if (!classRosterId) return;
    if (loadedRosterRef.current === classRosterId) return;
    loadedRosterRef.current = classRosterId;
    setLoading(true);
    setDataReady(false);

    try {
      const [traineeOpts, currRes, rosterRes, gridRes] = await Promise.all([
        fetchTraineeOptions(),
        getCurriculumStatus(),
        getClassRosterDetails(classRosterId),
        getClassTraineeMilestoneDetails(classRosterId),
      ]);

      setTraineeOptions(traineeOpts);

      if (currRes?.message === "Success") {
        setCurriculumOptions(currRes.data);
      }

      if (rosterRes?.message === "Success") {
        setRosterDetails(rosterRes.data);
        setProjectName(
          rosterRes.data.lpClassRequestProjects?.[0]?.project ?? "—",
        );
        if (rosterRes.data.curriculumStatus) {
          setCurriculumValue({
            LP_CurriculumStatusId: rosterRes.data.curriculumStatusId,
            CurriculumStatus: rosterRes.data.curriculumStatus,
          });
        }
      }

      if (gridRes?.message === "Success") {
        setRows(normalizeRows(gridRes.data));
      } else {
        showSnack("No records found for this Roster ID.", "warning");
      }

      try {
        const milRes = await getMilestoneByRosterId(classRosterId);
        if (milRes?.message === "Success" && milRes.data?.[0]) {
          setColumnDefs(
            Object.entries(milRes.data[0]).map(([name, raw]) => ({
              name,
              ...parseMilestoneType(raw),
            })),
          );
        }
      } catch {
        /* non-fatal */
      }

      setDataReady(true);
    } catch {
      showSnack("An error occurred while loading roster data.", "error");
    } finally {
      setLoading(false);
    }
  }, [classRosterId, fetchTraineeOptions, showSnack]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  // ─────────────────────────────────────────────────────────────────────────
  // BULK EDIT — field-state helpers + sequential apply handler
  //
  // Iterates every selected row x enabled field, calls the same save API
  // used by onCellEditComplete. Optimistic updates applied per-field; each
  // failure reverts only the affected field on the affected row.
  // ─────────────────────────────────────────────────────────────────────────

  const setBulkField = useCallback((field, key, val) => {
    setBulkFieldState((prev) => ({
      ...prev,
      [field]: { ...prev[field], [key]: val },
    }));
  }, []);

  // All fields eligible for bulk: static + dynamic milestone columns.
  // Dynamic columns are appended at render time from columnDefs state.
  const allBulkFields = useMemo(
    () => [
      ...BULK_EDITABLE_FIELDS,
      ...columnDefs.map((col) => ({
        field: col.name,
        label: col.name,
        type: col.type,          // multiselect | memo | date | singleselect | number | default
        options: col.options,    // for multiselect / singleselect
        isDynamic: true,
      })),
    ],
    [columnDefs],
  );

  const activeFields = allBulkFields.filter(
    (f) => bulkFieldState[f.field]?.enabled,
  );

  const closeBulkDialog = useCallback(() => {
    setBulkDialogVisible(false);
    setBulkFieldState(emptyBulkState());
    bulkOpsLeadOracleIdRef.current = null;
  }, []);

  const applyBulkEdit = useCallback(async () => {
    if (activeFields.length === 0) {
      showSnack("Enable at least one field to update.", "warning");
      return;
    }
    for (const f of activeFields) {
      const val = bulkFieldState[f.field]?.value;
      if (f.type === "boolean") {
        // Boolean fields are valid only when explicitly set to true or false.
        // null means the user enabled the checkbox but never chose Yes/No.
        if (val === null || val === undefined) {
          showSnack(`Please select Yes or No for "${f.label}" before applying.`, "warning");
          return;
        }
        continue;
      }
      if (f.type === "multiselect") {
        // Require at least one option selected. An empty multiselect in the
        // bulk dialog is almost certainly a mistake — the user enabled the
        // field but never picked a value. "Clear all" is not a supported
        // bulk intent; it can be done per-cell via the inline editor.
        const arr = Array.isArray(val) ? val : [];
        if (arr.length === 0) {
          showSnack(`Please select at least one value for "${f.label}" before applying.`, "warning");
          return;
        }
        continue;
      }
      if (val === null || val === undefined || val === "") {
        showSnack(`Please set a value for "${f.label}" before applying.`, "warning");
        return;
      }
      if ((f.type === "memo" || f.type === "textarea") && val.length > MEMO_MAX_LENGTH) {
        showSnack(
          `"${f.label}" exceeds ${MEMO_MAX_LENGTH} characters (${val.length}). Please shorten it.`,
          "warning",
        );
        return;
      }
    }

    setBulkSaving(true);
    setLoading(true);

    // Don't let a bulk-apply overwrite a row with a legacy, unparseable Date/Number value.
    const isLegacyUnparseableValue = (existingValue, fieldType) => {
      if (existingValue == null || existingValue === "" || existingValue === "null") {
        return false;
      }
      if (fieldType === "date") return !isValid(new Date(existingValue));
      if (fieldType === "number") return isNaN(Number(existingValue));
      return false;
    };
    let skippedLegacyCount = 0;

    // ── Build flat updates array: one entry per (row × field). ────────────
    const updates = [];
    for (const row of selectedRows) {
      for (const f of activeFields) {
        if (
          f.isDynamic &&
          (f.type === "date" || f.type === "number") &&
          isLegacyUnparseableValue(row[f.field], f.type)
        ) {
          skippedLegacyCount += 1;
          continue;
        }
        const rawValue = bulkFieldState[f.field]?.value;
        let serialized;
        if (f.field === "OperationLeadName") {
          // Bulk ops lead: serialize the OracleId captured at selection time,
          // not the display string stored in bulkFieldState.
          const oracleId = bulkOpsLeadOracleIdRef.current;
          if (!oracleId) {
            showSnack('Please select a valid person for "Operation Lead".', "warning");
            setBulkSaving(false);
            setLoading(false);
            return;
          }
          serialized = serializeValue(oracleId);
        } else if (f.field === "TraineeStatus") {
          serialized = serializeValue(rawValue);
        } else if (f.type === "boolean") {
          serialized = serializeValue(rawValue === true || rawValue === 1);
        } else {
          serialized = serializeValue(rawValue);
        }
        updates.push({
          classTraineeId: row.id,
          fieldToUpdate:  f.field,
          valueToUpdate:  serialized,
        });
      }
    }

    // ── Optimistic update: apply all patches immediately. ─────────────────
    const rowOptimisticPatches = {};
    for (const row of selectedRows) {
      const patch = {};
      for (const f of activeFields) {
        if (
          f.isDynamic &&
          (f.type === "date" || f.type === "number") &&
          isLegacyUnparseableValue(row[f.field], f.type)
        ) {
          continue;
        }
        const rawValue = bulkFieldState[f.field]?.value;
        if (f.field === "TraineeStatus") {
          const matchedOpt = traineeOptions.find((o) => o.value === rawValue);
          patch.TraineeStatusId = rawValue;
          patch.TraineeStatus   = matchedOpt?.label ?? row.TraineeStatus;
        } else if (f.type === "boolean") {
          patch[f.field] = rawValue === true || rawValue === 1;
        } else {
          patch[f.field] = rawValue;
        }
      }
      rowOptimisticPatches[row.id] = patch;
    }
    setRows((prev) =>
      prev.map((r) =>
        rowOptimisticPatches[r.id] ? { ...r, ...rowOptimisticPatches[r.id] } : r,
      ),
    );

    try {
      const res = await bulkUpdateClassTraineeMilestoneGridDetails(
        classRosterId,
        updates,
      );

      if (res?.message === "Success" || res?.message === "NotFound") {
        const skippedNote =
          skippedLegacyCount > 0
            ? ` ${skippedLegacyCount} cell(s) skipped -- existing value was recorded under a previous Data Type and needs individual review.`
            : "";
        showSnack(
          `Bulk update applied: ${selectedRows.length} row(s) × ${activeFields.length} field(s) updated.${skippedNote}`,
          "success",
        );
        if (Array.isArray(res?.data) && res.data.length > 0) {
          setRows(normalizeRows(res.data));
        }
      } else {
        // Full revert on failure
        setRows((prev) =>
          prev.map((r) => {
            const original = selectedRows.find((s) => s.id === r.id);
            return original ? { ...original } : r;
          }),
        );
        showSnack("Bulk update failed. No changes were saved.", "error");
      }
    } catch {
      setRows((prev) =>
        prev.map((r) => {
          const original = selectedRows.find((s) => s.id === r.id);
          return original ? { ...original } : r;
        }),
      );
      showSnack("An error occurred during bulk update.", "error");
    } finally {
      setBulkSaving(false);
      setLoading(false);
      setBulkDialogVisible(false);
      setBulkFieldState(emptyBulkState());
      setSelectedRows([]);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeFields, bulkFieldState, classRosterId, selectedRows, showSnack, traineeOptions]);

  // ─────────────────────────────────────────────────────────────────────────
  // CURRICULUM STATUS UPDATE
  // ─────────────────────────────────────────────────────────────────────────

  const handleCurriculumChange = async (selected) => {
    if (!selected) return;
    const previous = curriculumValue;
    setCurriculumValue(selected);
    try {
      const res = await updateClassRosterCurriculumStatus(
        classRosterId,
        selected.LP_CurriculumStatusId,
      );
      if (res?.message === "Success") {
        showSnack("Curriculum status updated.", "success");
      } else {
        setCurriculumValue(previous);
        showSnack("Could not update curriculum status.", "error");
      }
    } catch {
      setCurriculumValue(previous);
      showSnack("An internal error occurred.", "error");
    }
  };

  // ─────────────────────────────────────────────────────────────────────────
  // CELL EDIT SAVE HANDLER
  //
  // Replaces the entire onRowEditInit / onRowEditSave / onRowEditCancel
  // triplet plus makeTrackedEditor / editedValuesRef / originalRowsRef.
  //
  // PrimeReact fires onCellEditComplete after the user commits a cell edit
  // (Enter key or clicking a different cell).
  //
  // Event shape:
  //   e.rowData    — the row object as it stood when editing BEGAN (original)
  //   e.newRowData — the row object with all editorCallback changes merged in
  //   e.field      — the column field name
  //   e.newValue   — derived by PrimeReact from its internal editingRowData via
  //                  resolveFieldData(); unreliable for overlay editors
  //                  (Dropdown, Calendar, MultiSelect) where the internal state
  //                  can lag behind editorCallback.
  //
  // WHY newRowData[field] INSTEAD OF newValue:
  //   PrimeReact builds newRowData by merging every editorCallback call into
  //   a copy of the row.  This is always current regardless of editor type.
  //   newValue is derived separately inside a setState callback and can miss
  //   the last change for overlay-based editors — exactly the "save does
  //   nothing" symptom seen in production.
  //
  // IMPORTANT — onCellEditComplete placement:
  //   PrimeReact requires this handler on each <Column>, NOT on <DataTable>.
  //   When placed on <DataTable> it is silently ignored and the handler never
  //   fires.  We stamp it onto every editable column via withCellEdit() below
  //   rather than passing it as a DataTable-level prop.
  //
  // Strategy:
  //   1. Skip read-only fields
  //   2. Derive newValue from newRowData[field] (reliable for all editor types)
  //   3. Normalise booleans (API row may carry 0/1, editor emits true/false)
  //   4. Optimistically update local rows state for instant feedback
  //   5. Persist to API; revert on failure
  // ─────────────────────────────────────────────────────────────────────────

  const onCellEditComplete = useCallback(
    async (e) => {
      const { rowData, newRowData, field } = e;

      if (READ_ONLY_FIELDS.has(field)) return;

      const originalValue = rowData[field];

      // Read from newRowData — reliable for Dropdown, Calendar, MultiSelect.
      // Fall back to e.newValue for plain InputText/InputNumber columns where
      // newRowData may not be populated by older wrapper versions.
      const newValue =
        newRowData?.[field] !== undefined ? newRowData[field] : e.newValue;

      // ── No-op guard ──────────────────────────────────────────────────────
      // For TraineeStatus: the editor emits a numeric ID (newValue) but
      // originalValue is the label string from rowData.TraineeStatus.
      // Compare against TraineeStatusId instead so the guard is type-consistent.
      // For all other fields: normalise 0/1 booleans from API rows against
      // true/false from checkbox editors.
      const originalComparable =
        field === "TraineeStatus"
          ? rowData.TraineeStatusId
          : typeof originalValue === "number" &&
              (originalValue === 0 || originalValue === 1)
            ? Boolean(originalValue)
            : originalValue;

      if (newValue === originalComparable) return; // no-op — value unchanged

      // ── OpsLead special case ─────────────────────────────────────────────
      // editorCallback delivers the display string; the API needs the OracleId.
      // pendingOpsLeadRef holds the full person object set by OpsLeadEditor's
      // onPersonSelected callback.
      let serialized;
      let optimisticPatch = { [field]: newValue }; // default: update only the edited field

      if (field === "OperationLeadName") {
        const pending = pendingOpsLeadRef.current;
        if (!pending || pending.rowId !== rowData.id) {
          showSnack("No valid ops lead selected.", "info");
          return;
        }
        serialized = serializeValue(pending.person.OracleId);
        pendingOpsLeadRef.current = null;
      } else if (field === "TraineeStatus") {
        // ── TraineeStatus special case ──────────────────────────────────────
        // The editor emits a numeric TraineeStatusId (e.g. 9).
        // The API field is "TraineeStatus" and expects the ID as a string ("9").
        // After save we need to update both row fields so the body template
        // (which reads TraineeStatusId for the Tag label) stays consistent:
        //   TraineeStatusId — the numeric ID (drives body Tag label lookup)
        //   TraineeStatus   — the label string (drives filter + normalizeRows)
        serialized = serializeValue(newValue); // newValue is numeric ID → "9"
        const matchedOpt = traineeOptions.find((o) => o.value === newValue);
        optimisticPatch = {
          TraineeStatusId: newValue,
          TraineeStatus: matchedOpt?.label ?? rowData.TraineeStatus,
        };
      } else {
        serialized = serializeValue(newValue);
      }

      // ── Optimistic update ────────────────────────────────────────────────
      // Apply the new value(s) locally so the UI feels instant.
      // For TraineeStatus, optimisticPatch contains both TraineeStatusId and
      // TraineeStatus (label) so the body Tag resolves correctly without a
      // re-fetch.
      setRows((prev) =>
        prev.map((r) =>
          r.id === rowData.id ? { ...r, ...optimisticPatch } : r,
        ),
      );

      // ── Persist ──────────────────────────────────────────────────────────
      try {
        setLoading(true);
        const res = await updateClassTraineeMilestoneGridDetails(
          classRosterId,
          rowData.id,
          field,
          serialized,
        );

        // "NotFound" is a known backend quirk for TraineeStatus — treat as success
        if (res?.message === "Success" || res?.message === "NotFound") {
          showSnack(`${field} updated.`, "success");
        } else {
          const revertPatch = Object.fromEntries(
            Object.keys(optimisticPatch).map((k) => [k, rowData[k]]),
          );
          setRows((prev) =>
            prev.map((r) =>
              r.id === rowData.id ? { ...r, ...revertPatch } : r,
            ),
          );
          showSnack(`Could not save "${field}".`, "error");
        }
      } catch {
        const revertPatch = Object.fromEntries(
          Object.keys(optimisticPatch).map((k) => [k, rowData[k]]),
        );
        setRows((prev) =>
          prev.map((r) => (r.id === rowData.id ? { ...r, ...revertPatch } : r)),
        );
        showSnack("An internal error occurred.", "error");
      } finally {
        setLoading(false);
      }
    },
    [classRosterId, showSnack, traineeOptions],
  );

  // ─────────────────────────────────────────────────────────────────────────
  // withCellEdit — stamps onCellEditComplete and cellClassName onto a column.
  //
  // PrimeReact REQUIRES onCellEditComplete on each <Column>, not on
  // <DataTable>.  Passing it to <DataTable> is silently ignored.
  // Since PrimeReactDataGrid builds <Column> elements from the `columns`
  // prop array, it must spread each column object's onCellEditComplete onto
  // its <Column>.  We attach it here at the column-definition level so the
  // wrapper can pass it through without needing a whitelist change — as long
  // as the wrapper does `<Column {...col} />` (or equivalent spread).
  //
  // cellClassName="rg-editable-cell" is also added here so the CSS hover
  // pencil indicator can target editable cells specifically without touching
  // read-only cells.  PrimeReact applies cellClassName as a class on the <td>.
  //
  // Read-only columns (no `editor` prop) are returned unchanged.
  // ─────────────────────────────────────────────────────────────────────────

  const withCellEdit = useCallback(
    (colDef) => {
      if (!colDef.editor) return colDef;
      return {
        ...colDef,
        onCellEditComplete,
        cellClassName: "rg-editable-cell",
      };
    },
    [onCellEditComplete],
  );

  // ─────────────────────────────────────────────────────────────────────────
  // THIRD-PARTY TRACKING OVERLAY
  // ─────────────────────────────────────────────────────────────────────────

  const handleInfoClick = useCallback(
    async (e, rowData) => {
      if (!rowData.HasThirdPartyUpdate) return;

      // Capture the clicked DOM element synchronously — both e.currentTarget
      // (React synthetic) and nativeEvent.currentTarget (DOM) are nulled after
      // the event finishes dispatching.  e.target / e.nativeEvent.target is the
      // actual element that received the click and is safe across async boundaries.
      const anchorEl = e.currentTarget ?? e.nativeEvent.target;

      try {
        const res = await getSearchPopoverContent(rowData.ClassTraineeId);
        if (res?.message === "Success") {
          setPopoverHtml(res.data[0]?.TrackingDetails ?? "");
          overlayRef.current?.toggle({ currentTarget: anchorEl });
        }
      } catch {
        showSnack("Could not load tracking details.", "error");
      }
    },
    [showSnack],
  );

  // ─────────────────────────────────────────────────────────────────────────
  // EXPORT
  // ─────────────────────────────────────────────────────────────────────────

  const handleExport = useCallback(() => {
    const exportCols = [
      { field: "TraineeOracleId", headerName: "Oracle Id" },
      { field: "TraineeName", headerName: "Trainee Name" },
      { field: "TraineeStatus", headerName: "Trainee Status" },
      { field: "EmployeeStatus", headerName: "Employee Status" },
      { field: "TraineeComment", headerName: "Comment" },
      { field: "FTRR", headerName: "FTRR" },
      { field: "EndorsedToNestingStatus", headerName: "E2N" },
      { field: "E2P", headerName: "E2P" },
      { field: "OperationLeadName", headerName: "Operation Lead" },
      { field: "ProductionDate", headerName: "Production Date" },
      { field: "Project", headerName: "Project" },
      { field: "Site", headerName: "Site" },
      ...columnDefs.map((c) => ({ field: c.name, headerName: c.name })),
    ];
    exportAsCsv(rows, exportCols, "LPClassRoster");
  }, [rows, columnDefs]);

  // ─────────────────────────────────────────────────────────────────────────
  // COLUMN DEFINITIONS
  //
  // Cell-edit migration notes vs row-edit:
  //   • No makeTrackedEditor wrapper — PrimeReact cell edit correctly
  //     propagates editorCallback updates back through opts.rowData, so
  //     editors just read opts.rowData[field] directly.
  //   • No _rowEditor column — the frozen-right edit controls column is gone.
  //   • FTRR column still has no editor (read-only per spec).
  // ─────────────────────────────────────────────────────────────────────────

  const staticColumnDefs = useMemo(
    () => [
      // ── FROZEN LEFT ──────────────────────────────────────────────────────
      {
        field: "TraineeOracleId",
        header: "Oracle ID",
        frozen: true,
        alignFrozen: "left",
        sortable: true,
        style: { minWidth: "130px" },
        body: (row) => (
          <OracleIdTemplate rowData={row} onInfoClick={handleInfoClick} />
        ),
      },
      {
        field: "TraineeName",
        header: "Trainee Name",
        frozen: true,
        alignFrozen: "left",
        sortable: true,
        style: { minWidth: "200px" },
        body: (row) => (
          <TraineeNameTemplate rowData={row} onInfoClick={handleInfoClick} />
        ),
      },

      // ── SCROLLABLE COLUMNS ───────────────────────────────────────────────
      {
        field: "TraineeComment",
        header: "Comment",
        sortable: false,
        style: { minWidth: "200px" },
        body: (row) => (
          <span className={styles.commentCell}>
            {row.TraineeComment || "—"}
          </span>
        ),
        // No editor — read-only per READ_ONLY_FIELDS
      },
      {
        field: "EmployeeStatus",
        header: "Employee Status",
        sortable: true,
        style: { minWidth: "150px" },
        // No editor — read-only per READ_ONLY_FIELDS; onCellEditComplete guards it
      },
      {
        field: "TraineeStatus",
        header: "Trainee Status",
        sortable: true,
        filter: true,
        filterMatchMode: "equals",
        filterElement: (opts) => (
          <Dropdown
            value={opts.value}
            options={traineeOptions.map((o) => o.label)}
            onChange={(e) => opts.filterApplyCallback(e.value)}
            placeholder="Filter status"
            showClear
            className={styles.filterDropdown}
          />
        ),
        style: { minWidth: "160px" },
        body: (row) => (
          <TraineeStatusBodyTemplate
            rowData={row}
            traineeOptions={traineeOptions}
          />
        ),
        editor: (opts) => (
          <TraineeStatusEditor
            value={opts.rowData.TraineeStatusId}
            options={traineeOptions}
            editorCallback={opts.editorCallback}
          />
        ),
      },
      {
        field: "FTRR",
        header: "FTRR",
        sortable: true,
        style: { minWidth: "90px" },
        body: (row) => <FtrrReadonlyCell value={row.FTRR} />,
        // No editor — FTRR is read-only per spec
      },
      {
        field: "EndorsedToNestingStatus",
        header: "E2N",
        sortable: true,
        style: { minWidth: "90px" },
        body: (row) => (
          <BooleanIconTemplate value={row.EndorsedToNestingStatus} />
        ),
        editor: (opts) => (
          <Checkbox
            checked={!!opts.rowData.EndorsedToNestingStatus}
            onChange={(e) => opts.editorCallback(e.checked)}
          />
        ),
      },
      {
        field: "E2P",
        header: "E2P",
        sortable: true,
        style: { minWidth: "90px" },
        body: (row) => <BooleanIconTemplate value={row.E2P} />,
        editor: (opts) => (
          <Checkbox
            checked={!!opts.rowData.E2P}
            onChange={(e) => opts.editorCallback(e.checked)}
          />
        ),
      },
      {
        field: "OperationLeadName",
        header: "Operation Lead",
        sortable: true,
        style: { minWidth: "260px" },
        body: (row) => (
          <span>
            {row.OperationLeadName || (
              <span className={styles.emptyCell}>—</span>
            )}
          </span>
        ),
        editor: (opts) => (
          <OpsLeadEditor
            rowData={opts.rowData}
            editorCallback={opts.editorCallback}
            onPersonSelected={(person) => {
              pendingOpsLeadRef.current = { rowId: opts.rowData.id, person };
            }}
          />
        ),
      },
      {
        field: "ProductionDate",
        header: "Production Date",
        sortable: true,
        style: { minWidth: "180px" },
        body: (row) => <DateBodyTemplate value={row.ProductionDate} />,
        // DateEditor is now self-contained for validation — no onValidationError prop needed
        editor: (opts) => (
          <DateEditor
            value={opts.rowData.ProductionDate}
            editorCallback={opts.editorCallback}
          />
        ),
      },
      {
        field: "Project",
        header: "Project",
        sortable: true,
        style: { minWidth: "160px" },
        // No editor — read-only per READ_ONLY_FIELDS
      },
      {
        field: "Site",
        header: "Site",
        sortable: true,
        style: { minWidth: "130px" },
        // No editor — read-only per READ_ONLY_FIELDS
      },
    ],
    [traineeOptions, handleInfoClick],
  );

  const dynamicColumnDefs = useMemo(
    () =>
      columnDefs.map((col) => {
        const base = {
          field: col.name,
          header: col.name,
          sortable: true,
          style: {
            minWidth:
              col.type === "memo" || col.type === "multiselect"
                ? "280px"
                : "160px",
          },
        };

        switch (col.type) {
          case "multiselect":
            return {
              ...base,
              body: (row) => <MultiSelectBodyTemplate value={row[col.name]} />,
              editor: (opts) => (
                <MultiSelectEditor
                  value={opts.rowData[col.name]}
                  options={col.options}
                  editorCallback={opts.editorCallback}
                />
              ),
            };
          case "memo":
            return {
              ...base,
              body: (row) => {
                const text = row[col.name];
                return text ? (
                  <span
                    className={styles.memoDisplay}
                    title={text}
                  >
                    {text}
                  </span>
                ) : (
                  <span className={styles.emptyCell}>—</span>
                );
              },
              editor: (opts) => (
                <MemoEditor
                  value={opts.rowData[col.name]}
                  editorCallback={opts.editorCallback}
                />
              ),
            };
          case "date":
            return {
              ...base,
              body: (row) => <DateBodyTemplate value={row[col.name]} />,
              editor: (opts) => (
                <DateEditor
                  value={opts.rowData[col.name]}
                  editorCallback={opts.editorCallback}
                />
              ),
            };
          case "singleselect":
            return {
              ...base,
              body: (row) => (
                <>
                  {row[col.name] ?? <span className={styles.emptyCell}>—</span>}
                </>
              ),
              editor: (opts) => (
                <SingleSelectEditor
                  value={opts.rowData[col.name]}
                  options={col.options}
                  editorCallback={opts.editorCallback}
                />
              ),
            };
          case "number":
            return {
              ...base,
              body: (row) => (
                <>
                  {row[col.name] != null ? (
                    <span>{row[col.name]}</span>
                  ) : (
                    <span className={styles.emptyCell}>—</span>
                  )}
                </>
              ),
              editor: (opts) => (
                <NumberEditor
                  value={opts.rowData[col.name]}
                  editorCallback={opts.editorCallback}
                />
              ),
            };
          default:
            return {
              ...base,
              body: (row) => (
                <span>
                  {row[col.name] ?? <span className={styles.emptyCell}>—</span>}
                </span>
              ),
              editor: (opts) => (
                <InputText
                  value={opts.rowData[col.name] ?? ""}
                  onChange={(e) => opts.editorCallback(e.target.value)}
                  className={styles.textInput}
                />
              ),
            };
        }
      }),
    [columnDefs],
  );

  /**
   * Final columns array.
   * Prepends a frozen checkbox selection column, then stamps onCellEditComplete
   * onto every editable column via withCellEdit.
   */
  const allColumns = useMemo(
    () => [
      {
        selectionMode: "multiple",
        frozen: true,
        alignFrozen: "left",
        headerStyle: { width: "3rem" },
        style: { width: "3rem" },
        bodyStyle: { textAlign: "center" },
      },
      ...staticColumnDefs.map(withCellEdit),
      ...dynamicColumnDefs.map(withCellEdit),
    ],
    [staticColumnDefs, dynamicColumnDefs, withCellEdit],
  );

  // ─────────────────────────────────────────────────────────────────────────
  // BULK EDIT DIALOG
  // ─────────────────────────────────────────────────────────────────────────

  const bulkDialogFooter = (
    <div className={styles.bulkDialogFooter}>
      {activeFields.length > 0 && (
        <div className={styles.bulkSummary}>
          Will update{" "}
          <strong>{activeFields.map((f) => f.label).join(", ")}</strong>
          {" "}for <strong>{selectedRows.length} row(s)</strong>.
        </div>
      )}
      <div className={styles.bulkDialogActions}>
        <Button
          variant="outlined"
          size="small"
          onClick={closeBulkDialog}
          disabled={bulkSaving}
          className={styles.bulkCancelBtn}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          size="small"
          onClick={applyBulkEdit}
          disabled={bulkSaving || activeFields.length === 0}
          className={styles.bulkApplyBtn}
        >
          {bulkSaving ? "Applying…" : `Apply to ${selectedRows.length} row(s)`}
        </Button>
      </div>
    </div>
  );

  // BulkFieldRow is defined at module level — see comment block above RosterGridV2.
  // All closed-over values are passed as explicit props below.

  // ─────────────────────────────────────────────────────────────────────────
  // TOOLBAR
  // ─────────────────────────────────────────────────────────────────────────

  const toolbar = (
    <div className={styles.toolbar}>
      <div className={styles.toolbarLeft}>
        <Button
          variant="contained"
          size="small"
          startIcon={<DownloadOutlinedIcon />}
          className={styles.exportBtn}
          onClick={handleExport}
          disabled={!dataReady}
        >
          Export
        </Button>
        <Button
          variant="contained"
          size="small"
          startIcon={<EditOutlinedIcon />}
          className={styles.bulkEditBtn}
          onClick={() => setBulkDialogVisible(true)}
          disabled={!dataReady || selectedRows.length === 0}
        >
          Bulk Edit{selectedRows.length > 0 ? ` (${selectedRows.length})` : ""}
        </Button>
        <span className={styles.metaItem}>
          <span className={styles.metaLabel}>Roster:</span>
          <span className={styles.metaValue}>
            {rosterDetails?.classRosterId ?? "—"}
          </span>
        </span>
        <span className={styles.metaItem}>
          <span className={styles.metaLabel}>Class:</span>
          <span className={styles.metaValue}>
            {rosterDetails?.className ?? "—"}
          </span>
        </span>
        <span className={styles.metaItem}>
          <span className={styles.metaLabel}>Project:</span>
          <span className={styles.metaValue}>{projectName}</span>
        </span>
      </div>

      <div className={styles.toolbarRight}>
        <span className={styles.metaLabel}>Curriculum Status</span>
        <Dropdown
          value={curriculumValue}
          options={curriculumOptions}
          optionLabel="CurriculumStatus"
          dataKey="LP_CurriculumStatusId"
          onChange={(e) => handleCurriculumChange(e.value)}
          placeholder="Set status…"
          disabled={!dataReady}
          className={styles.curriculumDropdown}
          panelClassName="rg-dropdown-panel"
        />
      </div>
    </div>
  );

  // ─────────────────────────────────────────────────────────────────────────
  // RENDER
  // ─────────────────────────────────────────────────────────────────────────

  return (
    <>
      {loading ? <BackdropComponent /> : null}
      <div id="rg-roster-grid" className={styles.root}>
        <Tooltip target=".rg-info-icon" position="top" />

        {toolbar}

        {/* ── Bulk Edit Dialog ───────────────────────────────────────────── */}
        <Dialog
          header={`Bulk Edit — ${selectedRows.length} row(s) selected`}
          visible={bulkDialogVisible}
          onHide={closeBulkDialog}
          footer={bulkDialogFooter}
          className={`${styles.bulkDialog} rg-bulk-dialog`}
        >
          <p className={styles.bulkDialogHint}>
            Enable the fields you want to update. Only checked fields will be
            applied to all selected rows.
          </p>

          {/* ── Static editable columns ── */}
          <p className={styles.bulkSectionLabel}>Standard fields</p>
          <div className={styles.bulkGrid}>
            {BULK_EDITABLE_FIELDS.map((f) => (
              <BulkFieldRow
                key={f.field}
                fieldDef={f}
                enabled={bulkFieldState[f.field]?.enabled ?? false}
                value={bulkFieldState[f.field]?.value ?? null}
                onToggle={(checked) => setBulkField(f.field, "enabled", checked)}
                onValueChange={(val) => setBulkField(f.field, "value", val)}
                onOpsLeadSelect={(oracleId) => { bulkOpsLeadOracleIdRef.current = oracleId; }}
                traineeOptions={traineeOptions}
                styles={styles}
                minDate={ONE_YEAR_AGO}
              />
            ))}
          </div>

          {/* ── Dynamic milestone columns ── */}
          {columnDefs.length > 0 && (
            <>
              <p className={styles.bulkSectionLabel} style={{ marginTop: "14px" }}>Milestone columns</p>
              <div className={styles.bulkGridSingle}>
                {columnDefs.map((col) => {
                  const fd = { field: col.name, label: col.name, type: col.type, options: col.options };
                  return (
                    <BulkFieldRow
                      key={col.name}
                      fieldDef={fd}
                      enabled={bulkFieldState[col.name]?.enabled ?? false}
                      value={bulkFieldState[col.name]?.value ?? null}
                      onToggle={(checked) => setBulkField(col.name, "enabled", checked)}
                      onValueChange={(val) => setBulkField(col.name, "value", val)}
                      traineeOptions={traineeOptions}
                      styles={styles}
                      minDate={ONE_YEAR_AGO}
                    />
                  );
                })}
              </div>
            </>
          )}
        </Dialog>

        <PrimeReactDataGrid
          value={rows}
          dataKey="id"
          columns={allColumns}
          enableGlobalFilter
          enableScrollable
          scrollHeight="calc(100vh - 260px)"
          editMode="cell"
          selection={selectedRows}
          onSelectionChange={(e) => setSelectedRows(e.value)}
          filterprops={INITIAL_FILTER_PROPS}
          filterDisplay="menu"
          sortField="TraineeOracleId"
          sortOrder={1}
          // paginator
          // rows={100}
          // rowsPerPageOptions={[50, 100, 200]}

          showGridlines
          stripedRows
          resizableColumns
          columnResizeMode="fit"
          tableStyle={{ minWidth: "1400px" }}
          emptyMessage={
            <div className={styles.emptyState}>
              <InboxOutlinedIcon className={styles.emptyIcon} />
              <p>No records to display.</p>
            </div>
          }
        />

        <OverlayPanel
          ref={overlayRef}
          showCloseIcon
          dismissable
          className={styles.overlay}
        >
          <div
            className={styles.overlayContent}
            dangerouslySetInnerHTML={{ __html: popoverHtml }}
          />
        </OverlayPanel>
        <SnackBar snack={snack} snackReset={snackReset} />
      </div>
    </>
  );
};

RosterGridV2.propTypes = {
  classRosterId: PropTypes.number,
};

export default RosterGridV2;
